import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import parse from "html-react-parser";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import AlertBox from "../components/AlertBox";
import Button from "../components/Button";
import ModalPage from "../components/ModalPage";
import FullscreenLoader from "../components/FullscreenLoader";
import { fetchTemplates, readOidcSub, toFlatApplicationConfig } from "../api/applicationTemplates";
import { currentHostname } from "../api/playground";
import usePlaygroundAccess from "../hooks/usePlaygroundAccess";
import { getActiveOrNextPeriod } from "../helpers/period-utils";
import { PLAYGROUND_HOME, PlaygroundDoneState } from "../helpers/playground";
import { ApplicationType, ReportType } from "../helpers/types";
import { useAuthContext } from "../providers/AuthProvider";
import { useConfigContext } from "../providers/ConfigProvider";

// Valgt søknadstype for rapportene huskes i fanen, så man slipper å velge på
// nytt etter hver gjennomgang.
const APP_TYPE_KEY = 'lyfti_playground_application_type';

const readStoredAppType = (): number => {
    try {
        return Number(sessionStorage.getItem(APP_TYPE_KEY)) || 0;
    } catch {
        return 0;
    }
};

/**
 * Fanen «Lekeplass» på Din side: alle søknads- og rapportskjemaene hos
 * stiftelsen, også de som er skjult eller stengt for søkere.
 */
const Playground = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { sub } = useAuthContext();
    const { reportData, setDraftId, setReportDraftId } = useConfigContext();
    const access = usePlaygroundAccess();

    // `null` = laster, som ellers på Din side.
    const [applications, setApplications] = useState<ApplicationType[] | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [appType, setAppType] = useState<number>(readStoredAppType);

    const authSub = sub || readOidcSub();

    useEffect(() => {
        if (access !== true || !authSub) return;
        let cancelled = false;
        fetchTemplates(authSub, currentHostname(), { playground: true }).then((rows) => {
            if (cancelled) return;
            if (!rows) {
                setLoadFailed(true);
                setApplications([]);
                return;
            }
            const flat = rows
                .map((tpl) => toFlatApplicationConfig(tpl) as ApplicationType)
                .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
            setApplications(flat);
        });
        return () => {
            cancelled = true;
        };
    }, [access, authSub]);

    const chooseAppType = (value: number) => {
        setAppType(value);
        try {
            sessionStorage.setItem(APP_TYPE_KEY, String(value));
        } catch {
            /* ignore */
        }
    };

    if (access === false) return <Navigate to="/din-side" replace />;

    const reports = (reportData ?? []).filter((r) => r.classification !== 'request');
    const requests = (reportData ?? []).filter((r) => r.classification === 'request');

    const openApplication = (item: ApplicationType) => {
        setDraftId(null);
        navigate(`/lekeplass/soknad/${item.url}`);
    };

    const openReport = (item: ReportType) => {
        setReportDraftId(null);
        navigate(`/lekeplass/rapport/${appType}/${item.url}`);
    };

    return (
        <div className="row">
            <div className="col-12 col-lg-8 mb-4">
                <h4>{t('playground_title')}</h4>
                <p>{parse(t('playground_intro'))}</p>
            </div>

            {loadFailed && (
                <div className="col-12 mb-3">
                    <AlertBox classes="w-100" background="danger">
                        <h6 className="mb-0">{t('playground_load_failed')}</h6>
                    </AlertBox>
                </div>
            )}

            <div className="col-12 col-lg-6 mb-4">
                <h5 className="mb-3">{t('playground_applications')}</h5>
                {access === null || applications === null ? (
                    <p>{t('loading')}</p>
                ) : applications.length === 0 ? (
                    <p><i>{t('playground_none')}</i></p>
                ) : (
                    applications.map((item) => {
                        const period = getActiveOrNextPeriod(item.periods, new Date());
                        const closed = !(period?.isWithin ?? false) && !item.periods_text?.search_outside_periods;
                        const badges = [
                            item.published === false ? t('playground_hidden') : null,
                            closed ? t('playground_closed') : null,
                        ].filter(Boolean) as string[];

                        return (
                            <PlaygroundCard
                                key={item.type}
                                title={item.title}
                                badges={badges}
                                onClick={() => openApplication(item)}
                            />
                        );
                    })
                )}
            </div>

            <div className="col-12 col-lg-6 mb-4">
                <div className="mb-4">
                    <label className="form-label fw-medium" htmlFor="playgroundAppType">
                        {t('playground_application_type')}
                    </label>
                    <select
                        id="playgroundAppType"
                        className="form-select"
                        value={appType}
                        onChange={(e) => chooseAppType(Number(e.target.value))}
                    >
                        <option value={0}>{t('playground_application_type_none')}</option>
                        {(applications ?? []).map((item) => (
                            <option key={item.type} value={item.type}>
                                {item.title}
                            </option>
                        ))}
                    </select>
                    <div className="form-text">{t('playground_application_type_help')}</div>
                </div>

                <ReportList title={t('playground_reports')} items={reports} appType={appType} onOpen={openReport} />
                <ReportList title={t('playground_requests')} items={requests} appType={appType} onOpen={openReport} />
            </div>
        </div>
    );
};

const ReportList = ({ title, items, appType, onOpen }: {
    title: string;
    items: ReportType[];
    appType: number;
    onOpen: (item: ReportType) => void;
}) => {
    const { t } = useTranslation();
    if (items.length === 0) return null;

    return (
        <div className="mb-4">
            <h5 className="mb-3">{title}</h5>
            {items.map((item) => {
                // Rapporttypen er ikke slått på for søknadstypen som er valgt —
                // søkere med den typen får den aldri. Fortsatt klikkbar.
                const notEnabled = appType > 0 && item.enabled_for?.[String(appType)] === false;
                return (
                    <PlaygroundCard
                        key={item.type}
                        title={item.title}
                        badges={notEnabled ? [t('playground_not_enabled')] : []}
                        onClick={() => onOpen(item)}
                    />
                );
            })}
        </div>
    );
};

const PlaygroundCard = ({ title, badges, onClick }: { title: string; badges: string[]; onClick: () => void }) => (
    <a
        href="#"
        className="card mb-3 border text-decoration-none w-100 next-gift-card"
        onClick={(e) => {
            e.preventDefault();
            onClick();
        }}
    >
        <div className="card-body d-flex justify-content-between align-items-center">
            <div>
                <h5 className="card-title mb-1">{title}</h5>
                {badges.length > 0 && (
                    <p className="card-text text-muted small mb-0">{badges.join(' · ')}</p>
                )}
            </div>
            <span className="ms-2">
                <FontAwesomeIcon icon={["fal", "arrow-right"]} style={{ fontSize: 30 }} />
            </span>
        </div>
    </a>
);

/**
 * Vakt rundt `/lekeplass/*`: samme tilgangssjekk som fanen, så en direkte
 * lenke ikke åpner skjemaene for noen uten rolle hos stiftelsen.
 */
export const PlaygroundGuard = ({ children }: { children: React.ReactNode }) => {
    const access = usePlaygroundAccess();
    if (access === null) return <FullscreenLoader />;
    if (!access) return <Navigate to="/din-side" replace />;
    return <>{children}</>;
};

/** Der «Send inn» havner på lekeplassen. */
export const PlaygroundDone = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { title, retry } = (location.state ?? {}) as PlaygroundDoneState;

    return (
        <ModalPage showCheck={true}>
            <h2>{t('playground_done_title')}</h2>
            <p className="my-3">
                {title && <b>{title}. </b>}
                {t('playground_done_text')}
            </p>
            <div className="d-flex justify-content-center flex-column flex-md-row gap-3">
                {retry && (
                    <Button title={t('playground_try_again')} type="outline-primary" onClick={() => navigate(retry)} />
                )}
                <Button title={t('playground_back')} type="primary" rightIcon="arrow-right" onClick={() => navigate(PLAYGROUND_HOME)} />
            </div>
        </ModalPage>
    );
};

export default Playground;
